import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Bot, FileText, Package, LayoutTemplate, Hammer, TrendingUp, Check, ExternalLink,
} from "lucide-react";
import { Layout } from "../components/Layout";
import { HubCard } from "../components/HubCard";
import { PageRocket } from "../components/PageRocket";
import { BuilderLogo } from "../components/BuilderLogo";

const BUILDERS = [
  { name: "RapidNative", url: "https://www.rapidnative.com", recommended: true },
  { name: "Lovable", url: "https://lovable.dev" },
  { name: "Bolt.new", url: "https://bolt.new" },
  { name: "Replit", url: "https://replit.com" },
  { name: "Cursor", url: "https://cursor.com" },
  { name: "Firebase Studio", url: "https://firebase.studio" },
  { name: "Windsurf", url: "https://windsurf.com" },
];

export default function BuildHub() {
  const navigate = useNavigate();
  const soon = (label) => toast.info(`${label} — coming soon`, { description: "We're adding this to the Build Hub." });

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
        <div className="flex items-start justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/12 px-4 py-1.5 text-xs font-semibold text-[#B8901F] mb-4">
              🚀 Build Hub
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-[#0F0F0F]">
              Let's Build
            </h1>
            <p className="text-[#687280] mt-3 max-w-xl">
              Turn proven business ideas into real apps using AI. Everything you need to go from
              concept to product.
            </p>
          </div>
          <PageRocket className="w-36 lg:w-52 -mt-2" />
        </div>

        {/* Build With AI — Choose Your Builder */}
        <div className="bg-[#0F0F0F] rounded-[2rem] p-8 md:p-10 mb-10 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-60"
            style={{ background: "radial-gradient(circle at 85% 30%, rgba(212,175,55,0.2) 0%, rgba(212,175,55,0) 55%)" }}
          />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-medium text-[#D4AF37] mb-4">
              <Bot size={14} /> Build With AI
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white">Choose Your Builder</h2>
            <p className="text-[#A1A1AA] mt-2 max-w-lg text-sm">
              Take a Builder Prompt into your favorite AI development platform and ship faster.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              {BUILDERS.map((b) => (
                <button
                  key={b.name}
                  onClick={() => window.open(b.url, "_blank", "noopener,noreferrer")}
                  data-testid={`builder-${b.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                    b.recommended
                      ? "bg-[#D4AF37] text-[#0F0F0F] hover:bg-[#c5a030]"
                      : "bg-white/5 text-white border border-white/10 hover:bg-white/10"
                  }`}
                >
                  <BuilderLogo url={b.url} name={b.name} size={22} />
                  {b.name}
                  <ExternalLink size={14} />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <HubCard
            icon={FileText}
            title="Launch Blueprints"
            subtitle="Generate a full development blueprint for any idea."
            items={["Builder Prompts", "UI Structure", "Database", "Feature List", "Auth", "Tech Stack"]}
            onClick={() => navigate("/business-ideas")}
            index={0}
          />
          <HubCard
            icon={Package}
            title="Ready-to-Launch Apps"
            subtitle="Purchase complete, production-ready startup applications."
            onClick={() => navigate("/apps")}
            index={1}
          />
          <HubCard
            icon={LayoutTemplate}
            title="Templates"
            subtitle="Kickstart with polished building blocks."
            items={["Landing Pages", "Dashboards", "UI Kits"]}
            badge="Soon"
            onClick={() => soon("Templates")}
            index={2}
          />
          <HubCard
            icon={Hammer}
            title="Start a Custom Build"
            subtitle="Starter Build Service — we build your custom app."
            badge="Soon"
            onClick={() => soon("Custom Build Service")}
            index={3}
          />
          <HubCard
            icon={TrendingUp}
            title="Popular Builds This Week"
            subtitle="Trending startup ideas people are building right now."
            onClick={() => navigate("/business-ideas")}
            index={4}
          />
        </div>
      </section>
    </Layout>
  );
}
